import {
  Dialog as ShadcnDialog,
  DialogHeader as ShadcnDialogHeader,
  DialogTitle as ShadcnDialogTitle,
  DialogContent as ShadcnDialogContent,
  DialogFooter as ShadcnDialogFooter,
} from '@workspace/ui/components/dialog';
import type { ReactNode } from 'react';
import { useDialog } from './use-dialog';
import { Icon } from '../icon/icon';
import * as icons from '../icon/assets';
import { cn } from '@workspace/ui/lib/utils';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface DialogProps {
  children: ReactNode;
}

export function Dialog({ children }: DialogProps) {
  const { closeDialog } = useDialog();
  return (
    <ShadcnDialog
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeDialog();
        }
      }}
    >
      {children}
    </ShadcnDialog>
  );
}

interface HeaderProps {
  title: string;
  type: 'destructive' | 'question' | 'confirm';
  showIcon?: boolean;
}

function Header({ title, type, showIcon }: HeaderProps) {
  const iconMap = {
    question: 'HelpCircle',
    destructive: 'AlertTriangle',
    confirm: 'Check',
  };

  const colorMap = {
    question: 'var(--icon-icon-subdued)',
    destructive: 'var(--icon-icon-red)',
    confirm: 'var(--icon-icon-blue)',
  };

  const backgroundColorMap = {
    question: 'var(--surface-surface-gray-default)',
    destructive: 'var(--surface-surface-red-tint)',
    confirm: 'var(--surface-surface-blue-tint)',
  };

  return (
    <ShadcnDialogHeader className='p-6 pb-0'>
      <div>
        {showIcon && (
          <Icon
            name={iconMap[type] as keyof typeof icons}
            size={24}
            color={colorMap[type]}
            wrapperStyle={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '40px',
              height: '40px',
              marginBottom: '16px',
              borderRadius: '999px',
              backgroundColor: backgroundColorMap[type],
            }}
          />
        )}

        <ShadcnDialogTitle className='text-left text-text-default! text-size-3 font-bold leading-font-height-3 whitespace-pre-line'>
          {title}
        </ShadcnDialogTitle>
      </div>
    </ShadcnDialogHeader>
  );
}

function Content({ children, ...props }: React.ComponentProps<typeof ShadcnDialogContent>) {
  return (
    <ShadcnDialogContent
      onCloseAutoFocus={(e) => {
        e.preventDefault();
        document.body.style.pointerEvents = '';
      }}
      className={cn(
        'border-border rounded-[var(--radius-modal)] [&>button]:last:hidden p-0 border-none z-[200]',
        props.className,
      )}
      aria-describedby={undefined}
    >
      {children}
    </ShadcnDialogContent>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <ShadcnDialogTitle className='text-left text-text-default! text-size-3 font-bold leading-font-height-3'>
      {children}
    </ShadcnDialogTitle>
  );
}

function HiddenTitle({ children }: { children: ReactNode }) {
  return (
    <VisuallyHidden>
      <ShadcnDialogTitle className='text-left text-text-default! text-size-3 font-bold leading-font-height-3'>
        {children}
      </ShadcnDialogTitle>
    </VisuallyHidden>
  );
}

function Body({ children }: { children: ReactNode }) {
  return <div className='px-6'>{children}</div>;
}

function Footer({ children, ...props }: React.ComponentProps<typeof ShadcnDialogFooter>) {
  return (
    <ShadcnDialogFooter
      className={cn('border-t border-t-border-divider px-6 py-4 mt-2', props.className)}
    >
      {children}
    </ShadcnDialogFooter>
  );
}

Dialog.Header = Header;
Dialog.Content = Content;
Dialog.Title = Title;
Dialog.HiddenTitle = HiddenTitle;
Dialog.Body = Body;
Dialog.Footer = Footer;
